/** Legacy shared key kept only as a read fallback for pre-account-scoped choices. */
const LEGACY_CURRENT_LOCATION_KEY = 'erp24:seller-workspace:current-location';
const SELLER_LOCATION_STORAGE_EVENT = 'erp24:seller-workspace-location-change';

type SellerLocationStorageEventDetail = {
  storageKey: string | null;
  value: string;
};

export function sellerLocationStorageKey(accountId: string | null | undefined): string | null {
  return accountId ? `${LEGACY_CURRENT_LOCATION_KEY}:${accountId}` : null;
}

export function readStoredSellerLocation(storageKey: string | null): string {
  if (!storageKey) return '';
  try {
    return (
      localStorage.getItem(storageKey) ?? localStorage.getItem(LEGACY_CURRENT_LOCATION_KEY) ?? ''
    );
  } catch {
    return '';
  }
}

export function writeStoredSellerLocation(storageKey: string | null, value: string): void {
  if (!storageKey) return;
  try {
    if (value) {
      localStorage.setItem(storageKey, value);
      localStorage.removeItem(LEGACY_CURRENT_LOCATION_KEY);
    } else {
      localStorage.removeItem(storageKey);
    }
  } catch {
    // Ignore storage failures (private mode / quota); context just won't persist.
  }
  dispatchSellerLocationStorageEvent(storageKey, value);
}

export function subscribeStoredSellerLocation(
  storageKey: string | null,
  onStoreChange: () => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handleLocationChange = (event: Event) => {
    const detail = (event as CustomEvent<SellerLocationStorageEventDetail>).detail;
    if (detail.storageKey === storageKey) onStoreChange();
  };
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === storageKey || event.key === LEGACY_CURRENT_LOCATION_KEY) onStoreChange();
  };
  window.addEventListener(SELLER_LOCATION_STORAGE_EVENT, handleLocationChange);
  window.addEventListener('storage', handleStorageChange);
  return () => {
    window.removeEventListener(SELLER_LOCATION_STORAGE_EVENT, handleLocationChange);
    window.removeEventListener('storage', handleStorageChange);
  };
}

function dispatchSellerLocationStorageEvent(storageKey: string | null, value: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<SellerLocationStorageEventDetail>(SELLER_LOCATION_STORAGE_EVENT, {
      detail: { storageKey, value },
    }),
  );
}
